import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Calendar, User, Video, ShieldCheck, Clock, MessageCircleQuestion, Send, Award, BarChart3, MonitorPlay, Eye } from "lucide-react";
import { api, API, apiError, wsUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LiveVideoPlayer } from "../components/webinar/LiveVideoPlayer";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" }); } catch { return ""; } };

export default function WebinaireAcces() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [questions, setQuestions] = useState([]);
  const [sending, setSending] = useState(false);
  const [poll, setPoll] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [videoState, setVideoState] = useState({ source: null });
  const [viewers, setViewers] = useState(0);
  const wsRef = useRef(null);

  const loadQuestions = () => api.get(`/webinars/acces/${token}/questions`).then((r) => setQuestions(r.data.questions)).catch(() => {});
  const loadPoll = () => api.get(`/webinars/acces/${token}/poll`).then((r) => { setPoll(r.data.poll); setMyVote(r.data.my_vote); }).catch(() => {});
  useEffect(() => {
    const load = () => api.get(`/webinars/acces/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Lien invalide."));
    load(); loadQuestions(); loadPoll();
    const iv = setInterval(() => { load(); loadQuestions(); loadPoll(); }, 20000);
    return () => clearInterval(iv);
  }, [token]); // eslint-disable-line

  // --- Partage vidéo synchronisé : WebSocket + repli GET (correction de dérive) ---
  const wid = data?.webinar?.id;
  useEffect(() => {
    if (!wid || data?.webinar?.ended) return;
    let closed = false;
    const pull = () => api.get(`/webinars/acces/${token}/video`).then((r) => { setVideoState(r.data.video); if (typeof r.data.viewers === "number") setViewers(r.data.viewers); }).catch(() => {});
    pull();
    let ws;
    try {
      ws = new WebSocket(wsUrl(`/ws/webinars/${wid}`));
      wsRef.current = ws;
      ws.onmessage = (ev) => { try { const m = JSON.parse(ev.data); if (m.type === "video") setVideoState(m.state); else if (m.type === "viewers") setViewers(m.count); } catch {} };
      const ping = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); }, 25000);
      ws.onclose = () => clearInterval(ping);
    } catch {}
    const corr = setInterval(pull, 10000); // correction de dérive / repli si WS coupé
    return () => { closed = true; clearInterval(corr); try { ws && ws.close(); } catch {} };
  }, [wid, data?.webinar?.ended, token]); // eslint-disable-line

  const vote = async (i) => {
    try { await api.post(`/webinars/acces/${token}/poll/vote`, { option_index: i }); setMyVote(i); toast.success("Vote enregistré"); loadPoll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const ask = async () => {
    if (q.trim().length < 3) return;
    setSending(true);
    try { await api.post(`/webinars/acces/${token}/question`, { text: q }); setQ(""); toast.success("Question envoyée"); loadQuestions(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  if (err) return <div className="mx-auto max-w-lg px-4 py-16 text-center"><p className="text-slate-500">{err}</p><Link to="/webinaires" className="mt-4 inline-block text-ciel">← Retour aux webinaires</Link></div>;
  if (!data) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;
  const w = data.webinar;

  return (
    <div className="mx-auto max-w-2xl px-4 py-12" data-testid="webinaire-acces">
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ciel/10 px-3 py-1 text-xs font-semibold text-ciel"><ShieldCheck size={13} /> Accès personnel — Cabinet CARIS Santé</span>
        <h1 className="mt-4 font-display text-2xl font-bold text-navy">{w.title}</h1>
        <p className="mt-1 text-sm text-slate-500">Bonjour {data.name}, votre place est réservée.</p>
        <div className="mt-5 space-y-2 text-sm text-slate-600">
          {w.speaker && <p className="flex items-center justify-center gap-1.5"><User size={14} className="text-ciel" /> {w.speaker}</p>}
          <p className="flex items-center justify-center gap-1.5"><Calendar size={14} className="text-ciel" /> {fmt(w.starts_at)}</p>
        </div>
        <div className="mt-7">
          {w.ended ? (
            <a href={`${API}/webinars/certificate/${token}`} target="_blank" rel="noreferrer" data-testid="webinar-certificate-btn"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-base font-semibold text-white hover:bg-navy-light">
              <Award size={18} /> Télécharger mon certificat
            </a>
          ) : data.window_open && data.join_url ? (
            <a href={data.join_url} target="_blank" rel="noreferrer" data-testid="webinar-join-btn"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-8 py-4 text-base font-semibold text-white hover:bg-emerald-700">
              <Video size={18} /> Rejoindre le webinaire
            </a>
          ) : (
            <div className="rounded-2xl bg-slate-50 p-5 text-slate-500" data-testid="webinar-not-open">
              <Clock size={20} className="mx-auto mb-2 text-slate-400" />
              <p className="text-sm">Le lien de participation s'activera 15 minutes avant le début. Cette page se met à jour automatiquement.</p>
            </div>
          )}
        </div>
      </div>

      {!w.ended && videoState.source && (
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4" data-testid="webinar-live-video">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-navy"><MonitorPlay size={18} className="text-fuchsia-600" /> Vidéo partagée par l'intervenant</h2>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600" data-testid="webinar-viewers">
              <Eye size={13} className="text-fuchsia-600" /> {viewers} {viewers > 1 ? "participants regardent" : "participant regarde"}
            </span>
          </div>
          <LiveVideoPlayer state={videoState} mode="viewer" />
        </div>
      )}

      {!w.ended && poll && poll.active && (
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6" data-testid="webinar-poll">
          <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-navy"><BarChart3 size={18} className="text-emerald-600" /> Sondage en direct</h2>
          <p className="mb-3 text-sm font-medium text-slate-700">{poll.question}</p>
          <div className="space-y-2">
            {poll.options.map((o, i) => {
              const total = poll.counts.reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((poll.counts[i] / total) * 100);
              const mine = myVote === i;
              return (
                <button key={i} onClick={() => vote(i)} data-testid={`webinar-poll-option-${i}`}
                  className={`relative w-full overflow-hidden rounded-lg border px-3 py-2 text-left text-sm ${mine ? "border-emerald-500" : "border-slate-200 hover:border-emerald-300"}`}>
                  <span className="absolute inset-y-0 left-0 bg-emerald-50" style={{ width: `${pct}%` }} />
                  <span className="relative flex justify-between"><span>{mine ? "✓ " : ""}{o}</span><span className="text-slate-400">{pct}% ({poll.counts[i]})</span></span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">{poll.total} vote(s) — vous pouvez changer votre choix.</p>
        </div>
      )}

      {!w.ended && (
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6" data-testid="webinar-questions">
          <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-navy"><MessageCircleQuestion size={18} className="text-ciel" /> Poser une question au public / à l'intervenant</h2>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Votre question en direct…" data-testid="webinar-question-input" />
            <Button onClick={ask} disabled={sending || q.trim().length < 3} data-testid="webinar-question-send" className="rounded-full bg-ciel text-white hover:bg-sky-600"><Send size={15} /></Button>
          </div>
          <div className="mt-4 space-y-2">
            {questions.map((qq) => (
              <div key={qq.id} className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="text-[11px] font-semibold text-ciel">{qq.author}</p>
                <p className="text-slate-600">{qq.text}</p>
              </div>
            ))}
            {questions.length === 0 && <p className="text-xs text-slate-400">Aucune question pour le moment — soyez le premier !</p>}
          </div>
        </div>
      )}
    </div>
  );
}

